// A project's Project summary card, the whole of its Overview tab (owner, 2026-09-20; it was "Project details" over
// the place tally + gateways + stock counts, which read as three unrelated denominators — those live on the Sites,
// Assets and Stock counts tabs instead). Client, rental period with where today sits in it, how many sites and how
// many assets the project holds, and the note. A row without a value is left out; the two counts are always there.
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import DateRangeOutlinedIcon from '@mui/icons-material/DateRangeOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import { LinearProgress, Stack, Typography } from '@mui/material';
import { tally } from '@fleetspoke/core/sites';
import { useTranslation } from 'react-i18next';
import { useSession } from '../../auth/session';
import type { Project } from '../../data/billing';
import type { SiteOverview } from '../../data/queries';
import { isoDateIn } from '../../lib/billing';
import { projectTimeline } from '../../lib/projectDuration';
import { localeTag } from '../../lib/time';
import { SummaryCard, SummaryCount as Count, SummaryRow as Row } from './SummaryCard';

/** "1 Sep 2026" for a calendar date (YYYY-MM-DD). */
const longDate = (date: string | null, language: string) =>
  date
    ? new Intl.DateTimeFormat(localeTag(language), {
        timeZone: 'UTC',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      }).format(new Date(`${date}T00:00:00Z`))
    : '…';

export function ProjectDetails({
  project: p,
  locations,
  onOpenSites,
  onOpenAssets,
}: {
  project: Project;
  /** the project's active locations — their count and their assets are the summary's two figures */
  locations: SiteOverview[];
  onOpenSites?: () => void;
  onOpenAssets?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { me } = useSession();
  const today = isoDateIn(me?.company?.timezone ?? 'Asia/Kuala_Lumpur');
  const lang = i18n.language;
  const tl = projectTimeline(p.start_date, p.end_date, today, p.status === 'active');
  const figures = tally(locations);

  const range = `${longDate(p.start_date, lang)} – ${longDate(p.end_date, lang)}`;
  const length = 'total' in tl ? ` · ${t('projects.duration_days', { count: tl.total })}` : '';
  return (
    <SummaryCard title={t('projects.details')} testId="project-details">
      {p.client_name ? (
        <Row icon={<BusinessOutlinedIcon fontSize="small" />} label={t('projects.client')}>
          <Typography variant="body2">{p.client_name}</Typography>
        </Row>
      ) : null}
      {tl.kind !== 'none' ? (
        <Row icon={<DateRangeOutlinedIcon fontSize="small" />} label={t('projects.duration')}>
          <Typography variant="body2">
            {range}
            {length}
          </Typography>
          {tl.kind === 'running' ? (
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 0.75 }}>
              <LinearProgress
                variant="determinate"
                value={(tl.day / tl.total) * 100}
                sx={{ height: 6, borderRadius: 3, flexGrow: 1, maxWidth: 240 }}
                aria-label={t('projects.progress', {
                  day: tl.day,
                  total: tl.total,
                  left: tl.left,
                })}
              />
              <Typography variant="caption" color="text.secondary">
                {t('projects.progress', { day: tl.day, total: tl.total, left: tl.left })}
              </Typography>
            </Stack>
          ) : null}
          {tl.kind === 'upcoming' ? (
            <Typography variant="caption" color="text.secondary">
              {t('projects.starts_in', { count: tl.startsIn })}
            </Typography>
          ) : null}
          {tl.kind === 'overrun' ? (
            <Typography variant="caption" color="warning.main">
              {t('projects.overrun', { count: tl.overBy })}
            </Typography>
          ) : null}
        </Row>
      ) : null}
      <Row icon={<PlaceOutlinedIcon fontSize="small" />} label={t('projects.total_sites')}>
        <Count n={locations.length} onOpen={onOpenSites} />
      </Row>
      <Row icon={<Inventory2OutlinedIcon fontSize="small" />} label={t('projects.total_assets')}>
        <Count n={figures.expected} onOpen={onOpenAssets} />
      </Row>
      {p.note ? (
        <Row icon={<NotesOutlinedIcon fontSize="small" />} label={t('projects.note')}>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {p.note}
          </Typography>
        </Row>
      ) : null}
    </SummaryCard>
  );
}
